/**
 * Generic server-side DataTable for the D5 "runs" resource family.
 *
 * Wraps `@refinedev/react-table` (server-side pagination/sort via the Refine
 * dataProvider) and renders with shadcn `ui/table` primitives. Each resource's
 * `list.tsx` supplies its own `ColumnDef[]`. Kept dependency-light so it
 * compiles against pinned deps regardless of D1/D2 progress.
 */
import { useTable } from "@refinedev/react-table";
import type { BaseRecord, HttpError } from "@refinedev/core";
import { flexRender, type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  EmptyState,
  ErrorState,
  TableSkeletonRows,
  TableStateRow,
} from "@/components/ui/states";

export interface RunsDataTableProps<TData extends BaseRecord> {
  resource: string;
  columns: ColumnDef<TData>[];
  /** Default sort column (default `created_at`). */
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageSize?: number;
  /** Called when a row is clicked (typically navigate to show). */
  onRowClick?: (record: TData) => void;
  /** Message rendered when the result set is empty. */
  emptyMessage?: string;
}

export function RunsDataTable<TData extends BaseRecord>({
  resource,
  columns,
  sortField = "created_at",
  sortOrder = "desc",
  pageSize = 25,
  onRowClick,
  emptyMessage = "No records found.",
}: RunsDataTableProps<TData>) {
  const table = useTable<TData, HttpError>({
    columns,
    refineCoreProps: {
      resource,
      pagination: { pageSize, current: 1, mode: "server" },
      sorters: { initial: [{ field: sortField, order: sortOrder }] },
    },
  });

  const {
    getHeaderGroups,
    getRowModel,
    refineCore: { tableQuery },
  } = table;

  const isLoading = tableQuery.isLoading;
  const isError = tableQuery.isError;
  const rows = getRowModel().rows;

  const current = table.getState().pagination.pageIndex + 1;
  const pageCount = table.getPageCount();

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border">
        <Table>
          <TableHeader>
            {getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeletonRows columns={columns.length} />
            ) : isError ? (
              <TableStateRow columns={columns.length}>
                <ErrorState error={tableQuery.error} />
              </TableStateRow>
            ) : rows.length === 0 ? (
              <TableStateRow columns={columns.length}>
                <EmptyState message={emptyMessage} />
              </TableStateRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={onRowClick ? "cursor-pointer" : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Page {current} of {Math.max(pageCount, 1)}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
